import React, { useState, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import '../../components/Admin/adminShared.scss';

const isMobile = window.innerWidth < 576;
const API_STORAGE = process.env.REACT_APP_BASE_URL ? process.env.REACT_APP_BASE_URL.replace('/api/', '/storage/') : '/storage/';

interface UserItem {
    id: number;
    firstname: string;
    lastname: string;
    username: string;
    email: string;
    avatar: string | null;
    companyId: number;
}

const ShowAllUsers: React.FC<{ history: any }> = ({ history }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
    const [users, setUsers] = useState<UserItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);

    const currentUserId = Number(localStorage.getItem('mikaUserId'));

    const fetchUsers = async () => {
        try {
            const res = await axiosCaller('GET', 'admin/users');
            setUsers(res?.data?.data || []);
        } catch {} finally { setLoading(false); }
    };

    useEffect(() => { fetchUsers(); }, []);

    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(u =>
            (u.firstname + ' ' + u.lastname).toLowerCase().includes(q) ||
            u.username?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q)
        );
    }, [users, search]);

    const handleEdit = (user: UserItem) => {
        history.push(`/admin/create-user/${user.id}`);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await axiosCaller('DELETE', `admin/users/${deleteTarget.id}`);
            toast.success(t('admin.toast.userDeleted'));
            setDeleteTarget(null);
            fetchUsers();
        } catch {
            toast.error(t('admin.toast.somethingWentWrong'));
            setDeleteTarget(null);
        }
    };

    const getInitials = (user: UserItem) => {
        const f = user.firstname?.[0] || '';
        const l = user.lastname?.[0] || '';
        return (f + l).toUpperCase() || '?';
    };

    return (
        <div className="admin-page-container">
            {/* Header */}
            <div className="admin-header">
                <div className="admin-header-text">
                    <h1 className="admin-header-title"><FormattedMessage id="admin.users.pageTitle" defaultMessage="Përdoruesit" /></h1>
                    <p className="admin-header-subtitle"><FormattedMessage id="admin.users.pageSubtitle" defaultMessage="Menaxho përdoruesit e kompanisë" /></p>
                </div>
                <div className="admin-header-actions">
                    <button className="admin-btn-white" onClick={() => history.push('/admin/create-user')} type="button">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        <FormattedMessage id="admin.users.createUserBtn" defaultMessage="Krijo Përdorues" />
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="admin-search">
                <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    type="text"
                    className="admin-search-input"
                    placeholder={fm('admin.users.searchPlaceholder', 'Kërko përdorues...')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                    <button className="admin-search-clear" onClick={() => setSearch('')} type="button">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="admin-card">
                {loading ? (
                    <div className="admin-empty-state"><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></div>
                ) : filteredUsers.length === 0 ? (
                    <div className="admin-empty-state"><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></div>
                ) : (
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '45px' }}>#</th>
                                    <th><FormattedMessage id="admin.users.colUser" defaultMessage="Përdoruesi" /></th>
                                    <th className="admin-hide-mobile"><FormattedMessage id="admin.users.colUsername" defaultMessage="Username" /></th>
                                    <th className="admin-hide-mobile"><FormattedMessage id="admin.users.colEmail" defaultMessage="Email" /></th>
                                    <th style={{ width: '90px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user, i) => (
                                    <tr key={user.id} onClick={() => handleEdit(user)}>
                                        <td style={{ color: '#94a3b8', fontSize: '12px' }}>{i + 1}</td>
                                        <td>
                                            <div className="admin-user-cell">
                                                {user.avatar ? (
                                                    <img className="admin-avatar" src={`${API_STORAGE}${user.avatar}`} alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                ) : (
                                                    <div className="admin-avatar-initials">{getInitials(user)}</div>
                                                )}
                                                <span className="admin-user-name">{user.firstname} {user.lastname}</span>
                                            </div>
                                        </td>
                                        <td className="admin-hide-mobile">{user.username || '\u2014'}</td>
                                        <td className="admin-hide-mobile">{user.email || '\u2014'}</td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className="admin-table-actions">
                                                <button className="admin-table-icon-btn" onClick={() => handleEdit(user)} title={fm('common.action.edit', 'Ndrysho')} type="button">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                {user.id !== currentUserId && (
                                                    <button className="admin-table-icon-btn delete" onClick={() => setDeleteTarget(user)} title={fm('common.action.delete', 'Fshi')} type="button">
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Delete Confirmation */}
            <MikaConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={fm('admin.users.deleteTitle', 'Fshi përdoruesin')}
                message={deleteTarget ? intl.formatMessage(
                    { id: 'admin.users.deleteMessage', defaultMessage: 'A jeni i sigurt që dëshironi ta fshini {name}?' },
                    { name: `${deleteTarget.firstname} ${deleteTarget.lastname}` }
                ) : ''}
                confirmLabel={fm('common.action.delete', 'Fshi')}
                confirmColor="danger"
            />
        </div>
    );
};

export default withRouter(ShowAllUsers as any);
